/**
 * Editing one agent.
 *
 * THE PROMPT IS NOT A SECRET, and whoever types it should know. Two things make
 * it public: if a browser ever holds an `oai-events` data channel,
 * `session.created` hands the end user the instructions and every tool schema
 * verbatim; and the moment a public voice page exists, that leak is public by
 * definition. The design response is to keep authority in tool implementations
 * rather than in prose. The form used to state this in a warning beside the
 * textarea; the on-screen callout was removed on request, so this header is now
 * where the property is recorded.
 *
 * PATCH SENDS ONLY WHAT CHANGED. A PUT of the whole object would have to invent
 * values for the columns this form does not edit, and -- worse -- would clobber
 * a concurrent edit of a field the person never touched. The dirty diff also
 * gives the Save button something honest to be disabled by.
 *
 * SELECTS KEEP A VALUE THEY DO NOT RECOGNISE. If the row says the voice is
 * something not in the list below -- a model or voice shipped after this bundle
 * was built -- the current value is added as an option rather than being
 * silently replaced by the first entry. A form that quietly rewrites a field
 * nobody touched is the worst kind of form, and with a browser bundle that can
 * sit cached in a tab for hours while the server moves on, it is not
 * hypothetical.
 *
 * NO TOAST. Save status is a line next to the button, because that is where the
 * eye already is, and because a toast for the one action on the screen is a
 * notification about something the person is looking at.
 *
 * A VIEWER GETS THIS SCREEN AS A DOCUMENT, NOT AS A FORM. The whole page is
 * readable — knowing what an agent is configured with is exactly the sort of
 * thing a read-only role is for — but every field is inert and the action bar is
 * gone rather than disabled. Leaving the inputs live under a hidden Save button
 * would invite somebody to rewrite a twelve-row prompt and then discover there is
 * nowhere to put it, and a greyed-out Save promises that pressing it is a matter
 * of timing when for this role it never will be. PATCH on this row would answer
 * `read_only`, and no screen here should have to render that error.
 */
import { useEffect, useMemo, useState } from 'react'
import { CheckIcon, ChevronsUpDownIcon, PhoneCallIcon, PlayIcon, SquareIcon, XIcon } from 'lucide-react'
// The raw Select primitives, for VoiceSelect only: the ui wrapper's SelectItem
// puts its whole children inside ItemText, which the closed trigger echoes —
// an accent line and a play button do not belong in the field. Same import
// spelling as components/ui/select.tsx.
import { Select as SelectPrimitive } from 'radix-ui'
import {
  ApiError,
  agents,
  telephony,
  toolDefs as toolDefsApi,
  transferTargets as transferTargetsApi,
  webhooks as webhooksApi,
  type AgentConfig,
  type AgentPatch,
  type PhoneNumberView,
  type ToolDefView,
  type TransferTargetView,
  type WebhookView,
} from '@/api'
import { can } from '@/lib/auth'
import { telephonyMessageFor } from '@/lib/telephony-form'
import { useAuth } from '@/components/auth-provider'
import { InstructionsExpand } from '@/components/instructions-dialog'
import { GreetingGenerator } from '@/components/greeting-generator'
import { Link } from '@/components/app-sidebar'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { fmtWhen } from '@/components/state-pill'
import { cn } from '@/lib/utils'

/**
 * The choices this build knows about.
 *
 * `gpt-realtime-2.1` is GA and the default; `-mini` is the same API, the same
 * events and one config field, for roughly 58% less on audio tokens. That is why
 * the cheap tier is a product decision here rather than a migration later.
 *
 * `gpt-live-1` is a SECOND API SURFACE from the same provider, not a third tier:
 * full-duplex audio, native transcription, and reasoning delegated to a backend
 * Responses model. Several controls below switch with it — its own voice set, a
 * backend-model select that only it has, and two realtime settings that go inert.
 */
const MODELS = ['gpt-realtime-2.1', 'gpt-realtime-2.1-mini', 'gpt-live-1']

/**
 * Which family a model string selects. MUST MATCH `apiFamilyForModel` in
 * packages/agent/src/live-session-config.ts — the authority this bundle cannot
 * import (@voiceai/agent is server-only; scripts/check-boundaries.mjs enforces
 * the boundary), so it is restated BY HAND like the built-in registry below.
 * Exact match, never a prefix: `gpt-live-transcribe` is a transcription model
 * and must not drag the form into the live family.
 */
const isLiveModel = (model: string) => model === 'gpt-live-1'

/** Realtime voices. `marin` is the schema default. */
const VOICES = [
  'marin',
  'cedar',
  'alloy',
  'ash',
  'ballad',
  'coral',
  'echo',
  'sage',
  'shimmer',
  'verse',
]

/**
 * GPT-Live voices — ALL twenty-two, in the provider playground's own groups
 * and order ("Natural voices", then "Generated voices", as of its 2026-09-18
 * refresh; the first group was "Real voices" before). NOT disjoint from the
 * realtime set, as this file once claimed: a direct probe (2026-09-11) showed
 * live creates accepting `marin` and `alloy` with 201s — the "12 new voices"
 * of the launch were the natural-voice ADDITIONS, not the boundary.
 * Authority: `LIVE_NATURAL_VOICES` / `LIVE_GENERATED_VOICES` in
 * packages/agent/src/live-session-config.ts, restated by hand for the same
 * boundary reason as `isLiveModel`. Descriptions, accents and genders for
 * every voice come from the sample manifest, not from here.
 */
const LIVE_VOICES = [
  // Natural voices — the playground's order.
  'ripple',
  'vesper',
  'willow',
  'stone',
  'meridian',
  'gleam',
  'bossa',
  'tempo',
  'cedar',
  'marin',
  // Generated voices — the playground's order.
  'quartz',
  'alloy',
  'ash',
  'ballad',
  'coral',
  'echo',
  'sage',
  'shimmer',
  'verse',
  'beacon',
  'delta',
  'cinder',
]

const voicesFor = (model: string) => (isLiveModel(model) ? LIVE_VOICES : VOICES)

/** Each family's default voice, preselected IN THE DRAFT when a model switch
 *  strands the current voice on the other family's list. Saved on Save like any
 *  other edit — a family switch never PATCHes anything by itself. */
const DEFAULT_VOICE = { live: 'quartz', realtime: 'marin' } as const

/**
 * The sentinel for "platform default backend". Same rule as `NO_WEBHOOK` below:
 * Radix Select refuses an empty-string item value, and null is the wire's word
 * for the default. Safe because no provider model id is the bare word 'default'.
 */
const DEFAULT_BACKEND = 'default'

/**
 * GPT-Live backend models, with the provider's published pricing per 1M tokens.
 * What the voice model delegates to; the default when the row says null is
 * `DEFAULT_LIVE_BACKEND_MODEL` in packages/agent/src/live-session-config.ts —
 * gpt-5.6-terra — restated in the option label for the same boundary reason as
 * `isLiveModel`.
 */
const BACKEND_MODELS: { value: string; hint: string }[] = [
  { value: 'gpt-6-astra', hint: 'Deepest reasoning — $10/M in, $50/M out, 5× terra.' },
  { value: 'gpt-5.6-terra', hint: 'The platform default — $2/M in, $12/M out.' },
  { value: 'gpt-5.6-luna', hint: 'Fastest and cheapest — $0.20/M in, $1.20/M out, a tenth of terra.' },
]

/**
 * `gpt-live-transcribe` emits live partials, which is what makes the transcript
 * readable while a call is still running; `gpt-transcribe` is roughly a quarter
 * of the price but only lands after the turn commits.
 */
const TRANSCRIBE_MODELS = ['gpt-live-transcribe', 'gpt-transcribe']

const VAD_MODES: { value: 'server_vad' | 'semantic_vad'; label: string; hint: string }[] = [
  {
    value: 'server_vad',
    label: 'server_vad',
    hint: 'Endpoints on silence. Predictable, and the VAD wait is visible in the turn timeline.',
  },
  {
    value: 'semantic_vad',
    label: 'semantic_vad',
    hint: 'Endpoints when the model judges the turn finished. Fewer cut-offs mid-sentence, at the cost of a less predictable wait.',
  },
]

/**
 * The built-in tool registry, restated here BY HAND — one entry, `show_info`.
 *
 * DELIBERATELY HARDCODED rather than imported or fetched. The real declaration
 * is `TOOL_SCHEMAS` in @voiceai/agent, which web/ may not import — the boundary
 * rule in scripts/check-boundaries.mjs exists precisely so nothing that lives
 * beside server code ships to a browser. And one built-in is not yet worth a
 * route: `GET /api/tools` used to serve this list and was removed while the
 * registry was empty; the comment where it lived (workers/edge-api/src/index.ts)
 * says a route earns its keep back when the list is long enough to be data.
 * Drift is loud, not silent — the PATCH validates every name against the real
 * registry, so a name that stops existing there fails the Save as
 * `unknown_tool` rather than saving a switch that does nothing.
 */
const BUILTIN_SHOW_INFO = {
  name: 'show_info',
  label: 'screen cards',
  description:
    'The model can put a small formatted card — lists, links, reference numbers — on the caller’s screen beside the chat while it talks. Offered only where a card can be seen: the chat flag, and this panel’s own call page. On the other widgets — flag, bar, bars, orb, button, pill — the tool is withheld for the call and the prompt is told there is no screen; on a phone call the model is told so when it tries. Off means the model is never offered the tool anywhere.',
}

/** Adds `current` to a fixed option list when the server is ahead of this build. */
function withCurrent(options: string[], current: string): string[] {
  return options.includes(current) ? options : [current, ...options]
}

type SaveState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'error'; message: string }

export function AgentEditPage({ id }: { id: string }) {
  const { user } = useAuth()
  const [server, setServer] = useState<AgentConfig | null>(null)
  const [draft, setDraft] = useState<AgentConfig | null>(null)
  // The two panel-owned libraries the tools section chooses from. Fetched here
  // rather than inside the section so a failure is one state with one message.
  const [httpDefs, setHttpDefs] = useState<ToolDefView[] | null>(null)
  const [httpDefsError, setHttpDefsError] = useState<string | null>(null)
  const [hooks, setHooks] = useState<WebhookView[] | null>(null)
  const [hooksError, setHooksError] = useState<string | null>(null)
  const [xferTargets, setXferTargets] = useState<TransferTargetView[] | null>(null)
  const [xferError, setXferError] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<ApiError | Error | null>(null)
  const [save, setSave] = useState<SaveState>({ kind: 'idle' })
  const [toolPickerOpen, setToolPickerOpen] = useState(false)
  const [xferPickerOpen, setXferPickerOpen] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    agents
      .get(id, ac.signal)
      .then((a) => {
        setServer(a)
        setDraft(a)
        setLoadError(null)
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return
        setLoadError(e instanceof Error ? e : new Error(String(e)))
      })
    return () => ac.abort()
  }, [id])

  // The three panel-owned libraries, once each. Independent requests with
  // independent failures: the webhook selects must not go blank because the tool
  // list 500d, and the transfer picker must not go blank because either did.
  useEffect(() => {
    const ac = new AbortController()
    toolDefsApi
      .list(ac.signal)
      .then((res) => setHttpDefs(res.rows))
      .catch((e: unknown) => {
        if (ac.signal.aborted) return
        setHttpDefsError(e instanceof ApiError ? e.message : String(e))
      })
    transferTargetsApi
      .list(ac.signal)
      .then((res) => setXferTargets(res.rows))
      .catch((e: unknown) => {
        if (ac.signal.aborted) return
        setXferError(e instanceof ApiError ? e.message : String(e))
      })
    webhooksApi
      .list(ac.signal)
      .then((res) => setHooks(res.rows))
      .catch((e: unknown) => {
        if (ac.signal.aborted) return
        setHooksError(e instanceof ApiError ? e.message : String(e))
      })
    return () => ac.abort()
  }, [])

  /**
   * What to actually send. Compared field by field rather than deep-diffed:
   * there are fourteen of them, the three arrays share one comparison rule, and
   * a generic diff would be more code than the thing it generalises.
   */
  const patch = useMemo<AgentPatch>(() => {
    if (!server || !draft) return {}
    const p: AgentPatch = {}
    if (draft.name !== server.name) p.name = draft.name
    if (draft.instructions !== server.instructions) p.instructions = draft.instructions
    if (draft.voice !== server.voice) p.voice = draft.voice
    if (draft.model !== server.model) p.model = draft.model
    if (draft.greeting !== server.greeting) p.greeting = draft.greeting
    // Absent and 'spoken' are the same claim, like backendModel's null below.
    if ((draft.greetingMode ?? 'spoken') !== (server.greetingMode ?? 'spoken')) {
      p.greetingMode = draft.greetingMode ?? 'spoken'
    }
    if (draft.transcribeModel !== server.transcribeModel) {
      p.transcribeModel = draft.transcribeModel
    }
    if (draft.vadMode !== server.vadMode) p.vadMode = draft.vadMode
    // Null and ABSENT are the same claim — "platform default" — so they must not
    // diff against each other: a row written before the column existed carries no
    // field at all, and picking Default on it must not manufacture a change.
    if ((draft.backendModel ?? null) !== (server.backendModel ?? null)) {
      p.backendModel = draft.backendModel ?? null
    }
    // Sorted-join comparison, the same rule as the two id lists below. This one
    // holds built-in tool NAMES (`show_info` is the registry's one entry), and
    // like the transfer list it is a feature switch: a name off the list means
    // the model is never offered that tool, so clearing it must reach the server.
    const bt = [...draft.tools].sort().join(' ')
    const bs = [...server.tools].sort().join(' ')
    if (bt !== bs) p.tools = draft.tools
    // Sorted-join comparison because the checkboxes below build the array in
    // tick order, and re-ticking two boxes back to the saved set in a different
    // order is not a change worth a PATCH.
    const ht = [...draft.httpToolIds].sort().join(' ')
    const hs = [...server.httpToolIds].sort().join(' ')
    if (ht !== hs) p.httpToolIds = draft.httpToolIds
    // Same sorted-join reasoning. ⚠️ This list is also a FEATURE SWITCH: an empty
    // one means the agent's next call carries no transfer tool at all, so
    // clearing it is a real change and must reach the server as one.
    const xt = [...draft.transferTargetIds].sort().join(' ')
    const xs = [...server.transferTargetIds].sort().join(' ')
    if (xt !== xs) p.transferTargetIds = draft.transferTargetIds
    // Single-valued ids (or null), so plain comparison is the whole diff.
    if (draft.initWebhookId !== server.initWebhookId) p.initWebhookId = draft.initWebhookId
    if (draft.endWebhookId !== server.endWebhookId) p.endWebhookId = draft.endWebhookId
    if (draft.transferWebhookId !== server.transferWebhookId) {
      p.transferWebhookId = draft.transferWebhookId
    }
    return p
  }, [server, draft])

  const changed = Object.keys(patch)
  const dirty = changed.length > 0
  const readOnly = !can(user, 'write')

  if (loadError instanceof ApiError && loadError.notFound) {
    return (
      <div className="min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">No such agent</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Nothing is configured under <span className="mono">{id}</span>.{' '}
          <Link to="/agents" className="underline underline-offset-4">
            Back to the agent list
          </Link>
          .
        </p>
      </div>
    )
  }
  if (loadError) return <p className="err">{loadError.message}</p>
  if (!draft || !server) return <p className="loading">Loading agent...</p>

  /** Family of the DRAFT, not the saved row — the controls below must react the
   *  moment the model select changes, before anything is saved. */
  const live = isLiveModel(draft.model)

  /**
   * One setter, taking a partial rather than a (key, value) pair.
   *
   * A generic `set(key, value)` is the obvious shape and it is the wrong one
   * here: `{ ...draft, [key]: value }` with a generic key widens to an index
   * signature, so the compiler stops checking that `vadMode` only ever receives
   * one of its two literals — which is exactly the field where a typo would
   * survive to the server. A partial keeps every field's own type.
   */
  const set = (changes: Partial<AgentConfig>) => {
    setDraft({ ...draft, ...changes })
    // A new edit invalidates the previous outcome. Leaving "Saved" up while
    // somebody types the next change claims something that is no longer true.
    if (save.kind !== 'idle') setSave({ kind: 'idle' })
  }

  const onSave = () => {
    if (!dirty) return
    setSave({ kind: 'saving' })
    // NO AbortController TIED TO THIS COMPONENT, deliberately. A write must not
    // be cancelled because the reader navigated away mid-flight -- the request
    // either reached the server or it did not, and aborting it makes that
    // unknowable. A timeout signal bounds it instead.
    agents
      .update(id, patch, AbortSignal.timeout(15_000))
      .then((updated) => {
        setServer(updated)
        setDraft(updated)
        setSave({ kind: 'saved' })
      })
      .catch((e: unknown) => {
        setSave({ kind: 'error', message: e instanceof ApiError ? e.message : String(e) })
      })
  }

  /**
   * The tool picker's options, with a fallback rule applied twice over. When
   * the list is unreachable, the options are this agent's own saved ids —
   * removable, not addable, the safe direction. When the list IS here but a
   * saved id is not on it, the tool was revoked after being attached: an
   * option is still offered so it CAN be detached, because a reference that
   * exists nowhere on screen is a reference nobody can ever remove.
   */
  const httpToolOptions: { id: string; name: string; displayName?: string | null; description: string }[] =
    httpDefs === null
      ? draft.httpToolIds.map((id) => ({
          id,
          name: id,
          description: 'The tool list is unavailable. Id taken from this agent’s own configuration.',
        }))
      : [
          ...httpDefs.map((d) => ({ id: d.id, name: d.name, displayName: d.displayName, description: d.description })),
          ...draft.httpToolIds
            .filter((id) => !httpDefs.some((d) => d.id === id))
            .map((id) => ({
              id,
              name: id,
              description:
                'Not on the live list — revoked since it was attached. New sessions already skip it; untick to tidy the reference away. It cannot be re-ticked.',
            })),
        ]

  /**
   * The transfer picker's options, under the same two fallback rules as the tool
   * picker above and for the same reasons: an unreachable list offers this
   * agent's own saved ids so a desk can be REMOVED but not added, and a saved id
   * absent from the live list is still offered so a revoked desk can be tidied
   * away rather than being a reference nobody can see.
   *
   * A DISABLED target IS on the live list and IS offerable, unlike a revoked one.
   * That is the difference between the two switches: disabled is reversible and
   * an operator may legitimately want an agent pointed at a desk that is shut
   * today. The chip below says so.
   */
  const xferOptions: { id: string; name: string; description: string; enabled: boolean }[] =
    xferTargets === null
      ? draft.transferTargetIds.map((tid) => ({
          id: tid,
          name: tid,
          description: 'The transfer list is unavailable. Id taken from this agent’s own configuration.',
          enabled: true,
        }))
      : [
          ...xferTargets.map((t) => ({
            id: t.id,
            name: t.name,
            description: t.description,
            enabled: t.enabled,
          })),
          ...draft.transferTargetIds
            .filter((tid) => !xferTargets.some((t) => t.id === tid))
            .map((tid) => ({
              id: tid,
              name: tid,
              description:
                'Not on the live list — revoked since it was attached. New sessions already skip it; untick to tidy the reference away. It cannot be re-ticked.',
              enabled: false,
            })),
        ]

  return (
    <div className="min-w-0 max-w-4xl">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight">{server.name}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            <span className="mono">{server.id}</span> &middot; created {fmtWhen(server.createdAt)}{' '}
            &middot; last saved {fmtWhen(server.updatedAt)}
            {/* Absent on rows never edited since the column existed, and on
                edits made straight against the API — attribution only claims
                what the panel actually knows. */}
            {server.updatedBy ? <> by {server.updatedBy}</> : null}
          </p>
        </div>
        {/* The door to a live test, right where the config was just edited.
            Absent for a viewer (their session POST answers read_only), not
            disabled — same rule as every other write control here. */}
        {!readOnly && (
          <Button size="sm" asChild>
            <Link to={`/call?agent=${encodeURIComponent(server.id)}`}>
              <PhoneCallIcon className="size-3.5" />
              Call agent
            </Link>
          </Button>
        )}
      </div>
      {readOnly && (
        <p className="mb-4 max-w-2xl text-sm text-muted-foreground">
          Read-only: the <span className="mono">{user?.role}</span> role can see how this
          agent is configured but not change it, so the fields below are fixed and there is
          no Save.
        </p>
      )}

      <section className="panel">
        <h2>Identity</h2>
        <div className="body flex flex-col gap-4">
          <div className="field">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={draft.name}
              onChange={(e) => set({ name: e.target.value })}
              className="max-w-md"
              disabled={readOnly}
            />
            <p className="text-xs text-muted-foreground">
              Snapshotted onto every session row at call time, so renaming this does not
              rewrite history.
            </p>
          </div>

          <div className="field">
            <Label htmlFor="greeting">Greeting</Label>
            <Input
              id="greeting"
              value={draft.greeting ?? ''}
              placeholder="Leave empty to wait for the caller to speak first"
              onChange={(e) => set({ greeting: e.target.value === '' ? null : e.target.value })}
              className="max-w-2xl"
              disabled={readOnly}
            />
            <p className="text-xs text-muted-foreground">
              {live && draft.greetingMode === 'recorded'
                ? 'The words the agent says. In pre-recorded mode the agent speaks them once, in its own voice, and browser calls play that clip; phone calls, and any call where the clip cannot play, have the model speak them live.'
                : 'Spoken as turn zero. An empty greeting is a real choice and not a missing value: on the phone path a caller who hears silence usually speaks, and the resulting VAD wait is visible in the turn timeline.'}
            </p>
          </div>

          {live && (
            <div className="field">
              <Label htmlFor="greetingMode">Greeting style</Label>
              <Select
                value={draft.greetingMode ?? 'spoken'}
                onValueChange={(v) => set({ greetingMode: v as 'spoken' | 'recorded' })}
                disabled={readOnly}
              >
                <SelectTrigger id="greetingMode" className="w-full max-w-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="spoken">Spoken by the agent</SelectItem>
                  <SelectItem value="recorded">Pre-recorded audio</SelectItem>
                </SelectContent>
              </Select>
              <p className="max-w-md text-xs text-muted-foreground">
                {draft.greetingMode === 'recorded'
                  ? 'Browser calls open with a clip of the agent saying the greeting in its own voice, played the moment it loads, with the caller’s mic held silent until it ends, so a noisy room cannot delay it. The init webhook cannot change a clip.'
                  : 'The model says the greeting itself once the call connects.'}
              </p>
              {draft.greetingMode === 'recorded' && (
                <GreetingGenerator
                  agentId={server.id}
                  greeting={server.greeting}
                  voice={server.voice}
                  info={draft.greetingAudio ?? null}
                  unsaved={'greeting' in patch || 'voice' in patch || 'model' in patch}
                  readOnly={readOnly}
                  onChanged={(greetingAudio) => {
                    // Saved on its own endpoint, so it lands in BOTH copies and
                    // never shows up as an unsaved change.
                    // Functional updates: edits made while the upload ran stay.
                    setServer((cur) => (cur ? { ...cur, greetingAudio } : cur))
                    setDraft((cur) => (cur ? { ...cur, greetingAudio } : cur))
                  }}
                />
              )}
            </div>
          )}

          <div className="field">
            <div className="flex items-center justify-between">
              <Label htmlFor="instructions">Instructions</Label>
              <InstructionsExpand
                value={draft.instructions}
                onSave={(next) => set({ instructions: next })}
                readOnly={readOnly}
              />
            </div>
            {/* The system prompt is non-secret by design (a client holding an
                oai-events channel is handed it verbatim) — stated in the file
                header; the on-screen warning it used to carry was removed on
                request. Authority still belongs in tools, never in prose here. */}
            <Textarea
              id="instructions"
              value={draft.instructions}
              onChange={(e) => set({ instructions: e.target.value })}
              rows={6}
              className="max-h-48 overflow-y-auto font-mono text-[11px] leading-4"
              disabled={readOnly}
            />
            <p className="text-xs text-muted-foreground">
              {draft.instructions.length.toLocaleString('en-GB')} characters. Changing this
              mid-call busts the provider&rsquo;s prefix cache for the rest of that call --
              audio input goes from about $0.40 to $32 per 1M tokens -- which is why a
              handoff is a deliberate act and not a config tweak.
            </p>
          </div>
        </div>
      </section>

      <section className="panel">
        <h2>
          Model and audio
          <span className="note">these go into the initial session config, server-side</span>
        </h2>
        <div className="body grid2">
          <Choice
            id="model"
            label="Model"
            value={draft.model}
            options={withCurrent(MODELS, draft.model)}
            onChange={(v) => {
              // A family switch can strand the voice on the other family's
              // list, so the DRAFT preselects the new family's default. Only
              // the draft: it is saved on Save like any other edit, and
              // switching back before saving restores nothing automatically —
              // the person picked a model, not a voice.
              const changes: Partial<AgentConfig> = { model: v }
              if (!voicesFor(v).includes(draft.voice)) {
                changes.voice = isLiveModel(v) ? DEFAULT_VOICE.live : DEFAULT_VOICE.realtime
              }
              set(changes)
            }}
            disabled={readOnly}
            hint={
              live
                ? 'A second API surface, not a tier: full-duplex audio, native transcription, and reasoning delegated to the backend model below.'
                : 'Same API, same events and the same tool executor for both tiers. The -mini tier is roughly 58% cheaper on audio.'
            }
          />
          <VoiceSelect
            value={draft.voice}
            options={withCurrent(voicesFor(draft.model), draft.voice)}
            onChange={(v) => set({ voice: v })}
            disabled={readOnly}
            hint={
              // Reachable only on a row saved into the live family without a
              // live voice (an API edit, or a bundle older than the column):
              // the withCurrent rule keeps the value, and this line says what
              // the server will do with it.
              live && !LIVE_VOICES.includes(draft.voice)
                ? 'Not a GPT-Live voice — live calls fall back to quartz until a voice from this family is saved.'
                : undefined
            }
          />
          <Choice
            id="transcribeModel"
            label="Transcription"
            value={draft.transcribeModel}
            options={withCurrent(TRANSCRIBE_MODELS, draft.transcribeModel)}
            onChange={(v) => set({ transcribeModel: v })}
            // Disabled, not hidden, and the VALUE IS KEPT: the setting still
            // applies the moment the agent goes back to a realtime model, and a
            // form that cleared it on a family switch would be rewriting a
            // field nobody touched.
            disabled={readOnly || live}
            hint={
              live
                ? 'GPT-Live transcribes natively — this setting applies to realtime agents.'
                : 'Live partials make the transcript readable during a call; the cheaper model only lands after each turn commits.'
            }
          />
          <div className="field">
            <Label htmlFor="vadMode">Turn detection</Label>
            <Select
              value={draft.vadMode}
              onValueChange={(v) => set({ vadMode: v as AgentConfig['vadMode'] })}
              // Same keep-the-value rule as Transcription above.
              disabled={readOnly || live}
            >
              <SelectTrigger id="vadMode" className="w-full max-w-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VAD_MODES.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    <span className="mono">{m.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="max-w-md text-xs text-muted-foreground">
              {live
                ? 'GPT-Live is full-duplex — turn detection is provider-native.'
                : VAD_MODES.find((m) => m.value === draft.vadMode)?.hint}
            </p>
          </div>
          {live && (
            <div className="field">
              <Label htmlFor="backendModel">Backend model</Label>
              <Select
                value={draft.backendModel ?? DEFAULT_BACKEND}
                onValueChange={(v) => set({ backendModel: v === DEFAULT_BACKEND ? null : v })}
                disabled={readOnly}
              >
                <SelectTrigger id="backendModel" className="w-full max-w-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={DEFAULT_BACKEND}>
                    <span className="text-muted-foreground">Default (gpt-5.6-terra)</span>
                  </SelectItem>
                  {/* The withCurrent rule, inlined: a saved backend this build
                      does not know stays pickable rather than being silently
                      replaced by the first entry. */}
                  {typeof draft.backendModel === 'string'
                    && !BACKEND_MODELS.some((m) => m.value === draft.backendModel) && (
                      <SelectItem value={draft.backendModel}>
                        <span className="mono">{draft.backendModel}</span>
                      </SelectItem>
                    )}
                  {BACKEND_MODELS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      <span className="mono">{m.value}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="max-w-md text-xs text-muted-foreground">
                The voice model hands reasoning and tool calls to this model mid-conversation.{' '}
                {BACKEND_MODELS.find((m) => m.value === draft.backendModel)?.hint
                  ?? 'Default is gpt-5.6-terra — $2/M in, $12/M out.'}
              </p>
            </div>
          )}
        </div>
      </section>

      <section className="panel">
        <h2>
          Tools, transfers &amp; webhooks
          <span className="note">
            built-ins ticked here by name &middot; the rest defined on their own pages,
            referenced here by id &middot; resolved server-side when each session starts
          </span>
        </h2>
        <div className="body flex flex-col gap-5">
          {/* --------------------------------------------------- built-ins */}
          {/* Above the HTTP picker because that is the dispatch order: the
              session's executor checks the built-in registry before the DB
              list, and the panel refuses an HTTP definition named after a
              built-in — so this checkbox can never be shadowed by a row below. */}
          <div className="flex flex-col gap-3">
            <p className="max-w-2xl text-xs text-muted-foreground">
              Built-in tools ship with the platform and run inside the session itself
              &mdash; no endpoint of yours is called. There is exactly one, so it is a
              checkbox rather than a picker.
            </p>

            <div className="flex max-w-2xl items-start gap-2.5">
              <Checkbox
                id="builtinShowInfo"
                className="mt-0.5"
                checked={draft.tools.includes(BUILTIN_SHOW_INFO.name)}
                onCheckedChange={(on) =>
                  set({
                    // Add/remove just this name. `tools` can hold names this
                    // build does not know (a server ahead of this bundle), and
                    // overwriting the list with a literal would silently detach
                    // them — the withCurrent rule, applied to an array.
                    tools:
                      on === true
                        ? [...draft.tools, BUILTIN_SHOW_INFO.name]
                        : draft.tools.filter((n) => n !== BUILTIN_SHOW_INFO.name),
                  })
                }
                disabled={readOnly}
              />
              <div className="flex flex-col gap-1">
                <Label htmlFor="builtinShowInfo">
                  <span className="mono">{BUILTIN_SHOW_INFO.name}</span>
                  <span>&mdash; {BUILTIN_SHOW_INFO.label}</span>
                </Label>
                <p className="text-xs text-muted-foreground">
                  {BUILTIN_SHOW_INFO.description}
                </p>
              </div>
            </div>
          </div>

          {/* -------------------------------------------------- http tools */}
          <div className="flex flex-col gap-3">
            <p className="max-w-2xl text-xs text-muted-foreground">
              Tools defined on the <Link to="/tools" className="underline underline-offset-4">Tools</Link>{' '}
              page, each a POST to an endpoint of your own. The agent row stores only the
              ids ticked here — the URL, headers and schema are looked up fresh when a
              session starts, so editing a tool there changes the NEXT call of every agent
              that lists it.
            </p>

            {httpDefsError && (
              <p className="err text-xs">
                The tool list is unreachable ({httpDefsError}). The picker offers this
                agent&rsquo;s own saved ids, so a tool can be removed but not added.
              </p>
            )}

            {httpToolOptions.length === 0 ? (
              <p className="empty text-sm">
                No tools are defined, so there is nothing to attach. Define one on the
                Tools page first.
              </p>
            ) : (
              /*
                A MULTISELECT COMBOBOX, NOT A CHECKBOX PER TOOL. The library is
                system-wide and this form is per-agent: twenty registered tools
                would be twenty rows of other people's endpoints on every agent
                page. The popover stays open across picks (toggling is the whole
                point of a multiselect), the search matches name and description,
                and the chips below are the agent's actual selection — each
                removable in place.
              */
              <div className="flex max-w-2xl flex-col gap-2">
                <Popover open={toolPickerOpen} onOpenChange={setToolPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={toolPickerOpen}
                      className="w-full justify-between font-normal"
                      disabled={readOnly}
                    >
                      <span className="truncate text-muted-foreground">
                        {draft.httpToolIds.length === 0
                          ? 'No tools attached — search and pick…'
                          : `${draft.httpToolIds.length} of ${httpToolOptions.length} tools attached — search to change…`}
                      </span>
                      <ChevronsUpDownIcon className="size-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                  >
                    <Command>
                      <CommandInput placeholder="Search tools…" />
                      <CommandList>
                        <CommandEmpty>No tool matches.</CommandEmpty>
                        {httpToolOptions.map((t) => {
                          const on = draft.httpToolIds.includes(t.id)
                          return (
                            <CommandItem
                              key={t.id}
                              value={`${t.name} ${t.displayName ?? ''} ${t.description}`}
                              onSelect={() =>
                                set({
                                  httpToolIds: on
                                    ? draft.httpToolIds.filter((i) => i !== t.id)
                                    : [...draft.httpToolIds, t.id],
                                })
                              }
                            >
                              <CheckIcon
                                className={cn('size-4 shrink-0', on ? 'opacity-100' : 'opacity-0')}
                              />
                              <span className="min-w-0">
                                <span className="flex items-baseline gap-1.5 truncate text-sm">
                                  <span className="mono">{t.name}</span>
                                  {t.displayName && (
                                    <span className="truncate text-xs text-muted-foreground">
                                      {t.displayName}
                                    </span>
                                  )}
                                </span>
                                <span className="block truncate text-xs text-muted-foreground">
                                  {t.description}
                                </span>
                              </span>
                            </CommandItem>
                          )
                        })}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>

                {draft.httpToolIds.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {draft.httpToolIds.map((id) => {
                      const t = httpToolOptions.find((o) => o.id === id)
                      return (
                        <span
                          key={id}
                          className="pill pill-plain inline-flex items-center gap-1"
                          title={t?.description}
                        >
                          <span className="mono">{t?.name ?? id}</span>
                          {!readOnly && (
                            <button
                              type="button"
                              className="cursor-pointer rounded-full text-muted-foreground hover:text-foreground"
                              title={`Detach ${t?.name ?? id}`}
                              onClick={() =>
                                set({ httpToolIds: draft.httpToolIds.filter((i) => i !== id) })
                              }
                            >
                              <XIcon className="size-3" aria-hidden />
                              <span className="sr-only">Detach {t?.name ?? id}</span>
                            </button>
                          )}
                        </span>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ------------------------------------------------- transfers */}
          <div className="flex flex-col gap-3">
            {/* Both families offer the tool now. GPT-Live joined 2026-09-11:
                the handover was always carrier-side, and the DO grew a live
                fire trigger (the reflected-audio quiet watch) — the warning
                that used to sit here described the exclusion that preceded
                it. */}
            <p className="max-w-2xl text-xs text-muted-foreground">
              Desks from the{' '}
              <Link to="/transfers" className="underline underline-offset-4">
                Transfers
              </Link>{' '}
              page. On a <strong>phone call</strong>, the model may hand the caller to one of
              these &mdash; it chooses by name and never sees the number. Attaching none is not
              a neutral setting: an agent with no desks is given no transfer tool at all, so it
              cannot offer a person even when asked. Web callers cannot be transferred either
              way; there is no carrier leg to hand over.
            </p>

            {xferError && (
              <p className="err text-xs">
                The transfer list is unreachable ({xferError}). The picker offers this
                agent&rsquo;s own saved ids, so a desk can be removed but not added.
              </p>
            )}

            {xferOptions.length === 0 ? (
              <p className="empty text-sm">
                No transfer targets are defined, so there is nothing to attach and this agent
                cannot hand a caller to a person. Define one on the Transfers page first.
              </p>
            ) : (
              /*
                The tools picker's shape, for the tools picker's reason: the desk
                library is system-wide and this form is per-agent. The ORDER of
                the chips is the order the model is offered them, which is worth
                knowing but not worth a drag handle — the descriptions are what
                the model actually chooses on.
              */
              <div className="flex max-w-2xl flex-col gap-2">
                <Popover open={xferPickerOpen} onOpenChange={setXferPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={xferPickerOpen}
                      className="w-full justify-between font-normal"
                      disabled={readOnly}
                    >
                      <span className="truncate text-muted-foreground">
                        {draft.transferTargetIds.length === 0
                          ? 'No desks attached — this agent cannot transfer…'
                          : `${draft.transferTargetIds.length} of ${xferOptions.length} desks attached — search to change…`}
                      </span>
                      <ChevronsUpDownIcon className="size-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                  >
                    <Command>
                      <CommandInput placeholder="Search desks…" />
                      <CommandList>
                        <CommandEmpty>No desk matches.</CommandEmpty>
                        {xferOptions.map((t) => {
                          const on = draft.transferTargetIds.includes(t.id)
                          return (
                            <CommandItem
                              key={t.id}
                              value={`${t.name} ${t.description}`}
                              onSelect={() =>
                                set({
                                  transferTargetIds: on
                                    ? draft.transferTargetIds.filter((i) => i !== t.id)
                                    : [...draft.transferTargetIds, t.id],
                                })
                              }
                            >
                              <CheckIcon
                                className={cn('size-4 shrink-0', on ? 'opacity-100' : 'opacity-0')}
                              />
                              <span className="min-w-0">
                                <span className="block truncate text-sm font-medium">
                                  {t.name}
                                  {!t.enabled && (
                                    <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                                      (disabled)
                                    </span>
                                  )}
                                </span>
                                <span className="block truncate text-xs text-muted-foreground">
                                  {t.description}
                                </span>
                              </span>
                            </CommandItem>
                          )
                        })}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>

                {draft.transferTargetIds.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {draft.transferTargetIds.map((tid) => {
                      const t = xferOptions.find((o) => o.id === tid)
                      return (
                        <span
                          key={tid}
                          className="pill pill-plain inline-flex items-center gap-1"
                          title={
                            t?.enabled === false
                              ? `${t?.description ?? tid} — this desk is disabled, so no call will reach it until it is switched back on.`
                              : t?.description
                          }
                        >
                          <span>{t?.name ?? tid}</span>
                          {t?.enabled === false && (
                            <span className="text-xs text-muted-foreground">off</span>
                          )}
                          {!readOnly && (
                            <button
                              type="button"
                              className="cursor-pointer rounded-full text-muted-foreground hover:text-foreground"
                              title={`Detach ${t?.name ?? tid}`}
                              onClick={() =>
                                set({
                                  transferTargetIds: draft.transferTargetIds.filter((i) => i !== tid),
                                })
                              }
                            >
                              <XIcon className="size-3" aria-hidden />
                              <span className="sr-only">Detach {t?.name ?? tid}</span>
                            </button>
                          )}
                        </span>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* --------------------------------------------------- webhooks */}
          <div className="flex flex-col gap-3">
            <p className="max-w-2xl text-xs text-muted-foreground">
              Endpoints from the <Link to="/webhooks" className="underline underline-offset-4">Webhooks</Link>{' '}
              page, at three different moments. The <strong>init</strong> webhook fires before
              the model connects &mdash; it receives the caller&rsquo;s URL params and the
              prompt, and may return a rewritten prompt and greeting; if it fails or times
              out, the call proceeds with the originals. The <strong>transfer</strong> webhook
              fires mid-call, as a caller is handed to a human. The{' '}
              <strong>end-of-session</strong> webhook receives the transcript and stats after
              the recording finalises. The same endpoint can serve more than one slot.
            </p>

            {hooksError && (
              <p className="err text-xs">
                The webhook list is unreachable ({hooksError}). The selects below can keep
                or clear the current choice but not offer new ones.
              </p>
            )}

            <div className="grid2">
              <WebhookSelect
                id="initWebhookId"
                label="Init webhook"
                value={draft.initWebhookId}
                hooks={hooks}
                disabled={readOnly}
                onChange={(v) => set({ initWebhookId: v })}
                hint="Fires on EVERY path, telephony included, with a hard timeout — a slow or dead endpoint costs the call nothing but its overrides."
              />
              <WebhookSelect
                id="endWebhookId"
                label="End-of-session webhook"
                value={draft.endWebhookId}
                hooks={hooks}
                disabled={readOnly}
                onChange={(v) => set({ endWebhookId: v })}
                hint="Fires from the background jobs worker after finalisation, with retries — minutes after the caller hung up, not instantly."
              />
              <WebhookSelect
                id="transferWebhookId"
                label="Transfer webhook"
                value={draft.transferWebhookId}
                hooks={hooks}
                disabled={readOnly}
                onChange={(v) => set({ transferWebhookId: v })}
                hint="Fires in the second between hanging up the AI and the desk's phone ringing, so the receiving system can have the call on screen before the person says hello. Carries the transcript so far; no cost or recordings, because neither exists yet. Its response is IGNORED — it cannot cancel or redirect a transfer."
              />
            </div>
          </div>
        </div>
      </section>

      <section className="panel">
        <h2>
          Phone numbers
          <span className="note">
            managed on the Telephony page &middot; attach and detach apply immediately,
            separate from Save
          </span>
        </h2>
        <div className="body">
          {/* Its own component with its own state, DELIBERATELY outside this
              page's draft/patch machinery: attaching a number is a write to the
              NUMBER row via the telephony client, effective on the next inbound
              call — putting it in the draft would invent a Save step that does
              not exist and make the dirty count lie. */}
          <AgentPhoneNumbers agentId={server.id} readOnly={readOnly} />
        </div>
      </section>

      {/* The action bar. STICKY, because the instructions box is twelve rows tall
          and a Save button below it is off screen exactly when it is wanted.
          ABSENT ENTIRELY for a role that cannot write — see the file header. */}
      {readOnly ? null : (
      <div className="sticky bottom-0 -mx-4 flex flex-wrap items-center gap-3 border-t bg-background/95 px-4 py-3 backdrop-blur md:-mx-6 md:px-6">
        <Button onClick={onSave} disabled={!dirty || save.kind === 'saving'}>
          {save.kind === 'saving'
            ? 'Saving...'
            : dirty
              ? `Save ${changed.length} change${changed.length === 1 ? '' : 's'}`
              : 'Saved'}
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            setDraft(server)
            setSave({ kind: 'idle' })
          }}
          disabled={!dirty}
        >
          Discard
        </Button>
        {save.kind === 'saved' && (
          <span className="text-sm text-ev-good">
            Saved. It applies to the NEXT call -- a session already running holds the config
            it was created with.
          </span>
        )}
        {save.kind === 'error' && <span className="text-sm text-ev-bad">{save.message}</span>}
        {save.kind === 'idle' && dirty && (
          <span className="text-sm text-muted-foreground">
            Unsaved: {changed.join(', ')}
          </span>
        )}
      </div>
      )}
    </div>
  )
}

/** A labelled select over a list of strings. Three callers, so it exists. */
/**
 * ▶ HEAR A VOICE BEFORE SAVING IT ONTO AN AGENT. The samples are the
 * provider's own per-voice previews from cdn.openai.com/API/voice-previews —
 * the exact clips the OpenAI playground plays for the same decision —
 * downloaded and SELF-HOSTED as panel assets under /voice-samples/ (URL drift
 * on someone else's CDN must not break our picker, and an operator's browser
 * should not fetch from a third party to render a form). manifest.json is the
 * authority on which voices have a sample, in which container (wav or flac —
 * browsers play both), and what the playground says about each voice: a
 * one-line description, a language and accent, and a gender, all verbatim
 * chips from its picker (captured 2026-09-18); each entry also records its
 * source URL, because a file with no provenance is just bytes. A voice
 * without a sample — one newer than the last download — renders a quiet note,
 * never a broken button; scripts/generate-voice-samples.mjs can still GENERATE
 * a sample from the model itself for exactly that gap. Fetched once per page
 * load and shared.
 */
interface VoiceSampleEntry {
  format?: string
  /** "Warm and conversational" — the playground's subtitle for the voice. */
  description?: string
  /** "English" / "Portuguese" — the playground's language chip. */
  language?: string
  /** "Australian influenced" — the playground's accent chip. */
  accent?: string
  /** "Male" / "Female" — the playground's gender chip. */
  gender?: string
  /** The playground's own grouping: 'natural' (recorded from actors; it said
   *  'real' before the 2026-09-18 refresh) or 'generated' (synthesized). */
  provenance?: string
}
let sampleManifestPromise: Promise<Record<string, VoiceSampleEntry>> | null = null
function loadSampleManifest(): Promise<Record<string, VoiceSampleEntry>> {
  sampleManifestPromise ??= fetch('/voice-samples/manifest.json')
    .then((r) => (r.ok ? (r.json() as Promise<Record<string, VoiceSampleEntry>>) : {}))
    .catch(() => ({}))
  return sampleManifestPromise
}

/**
 * ONE SAMPLE AT A TIME, module-wide. The dropdown rows and the trigger-side
 * preview button are separate components; without a shared stop, two voices
 * demo neither. `pause` fires the element's own onpause, which is how the
 * component that started a sample learns another one silenced it.
 */
let currentSample: HTMLAudioElement | null = null
function stopCurrentSample() {
  currentSample?.pause()
  currentSample = null
}
function playSample(voice: string, entry: VoiceSampleEntry, onDone: () => void) {
  stopCurrentSample()
  const el = new Audio(`/voice-samples/${voice}.${entry.format ?? 'wav'}`)
  el.onended = onDone
  el.onerror = onDone
  el.onpause = onDone
  currentSample = el
  void el.play().catch(onDone)
}

/**
 * The voice picker, shaped like the provider's own playground: each option
 * carries its description, its language · accent · gender line, and a play
 * button that previews WITHOUT selecting.
 *
 * Composed from the Radix primitives directly rather than the ui SelectItem,
 * because that wrapper puts its whole children inside ItemText — and ItemText
 * is what the CLOSED trigger renders, so the label lines and a button would be
 * echoed into the field. Here ItemText carries the bare name and everything
 * else sits beside it. The play button swallows pointerdown/up/click so Radix
 * never reads a preview as a selection.
 */
function VoiceSelect({
  value,
  options,
  onChange,
  disabled,
  hint,
}: {
  value: string
  options: string[]
  onChange: (v: string) => void
  disabled?: boolean
  hint?: string
}) {
  const [manifest, setManifest] = useState<Record<string, VoiceSampleEntry> | null>(null)
  const [playing, setPlaying] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void loadSampleManifest().then((m) => {
      if (!cancelled) setManifest(m)
    })
    // Unmount must not leave a voice talking.
    return () => {
      cancelled = true
      stopCurrentSample()
    }
  }, [])

  const toggle = (voice: string) => {
    if (playing === voice) {
      stopCurrentSample()
      setPlaying(null)
      return
    }
    const entry = manifest?.[voice]
    if (!entry) return
    setPlaying(voice)
    playSample(voice, entry, () => setPlaying((p) => (p === voice ? null : p)))
  }

  /** Preview must never select: Radix items commit on pointer-up and click,
   *  so the button eats all three phases. */
  const swallow = (e: { stopPropagation: () => void; preventDefault: () => void }) => {
    e.stopPropagation()
    e.preventDefault()
  }

  const item = (voice: string) => {
    const entry = manifest?.[voice]
    return (
      <SelectPrimitive.Item
        key={voice}
        value={voice}
        className="relative flex w-full cursor-default items-center gap-2 rounded-md py-1.5 pr-2 pl-1.5 text-sm outline-hidden select-none focus:bg-accent focus:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50"
      >
        <span className="flex w-4 shrink-0 items-center justify-center">
          <SelectPrimitive.ItemIndicator>
            <CheckIcon className="size-4" />
          </SelectPrimitive.ItemIndicator>
        </span>
        <span className="min-w-0 flex-1">
          <SelectPrimitive.ItemText>
            <span className="mono">{voice}</span>
          </SelectPrimitive.ItemText>
          {entry?.description && <span className="ml-2 text-xs text-muted-foreground">{entry.description}</span>}
          {(() => {
            // The playground's three chips as one quiet line. Any subset the
            // manifest has: an older manifest carried only the accent.
            const traits = [entry?.language, entry?.accent, entry?.gender].filter(Boolean)
            return traits.length > 0 ? (
              <span className="block text-xs text-muted-foreground">{traits.join(' · ')}</span>
            ) : null
          })()}
        </span>
        {entry && (
          <button
            type="button"
            aria-label={`Play ${voice} preview`}
            className="flex size-6 shrink-0 items-center justify-center rounded-full border text-muted-foreground hover:text-foreground"
            onPointerDown={swallow}
            onPointerUp={swallow}
            onClick={(e) => {
              swallow(e)
              toggle(voice)
            }}
          >
            {playing === voice ? <SquareIcon className="size-3" /> : <PlayIcon className="size-3" />}
          </button>
        )}
      </SelectPrimitive.Item>
    )
  }

  /**
   * The playground's own grouping, from the manifest's provenance field:
   * natural voices (recorded from actors) first, then generated, then anything
   * the manifest does not know (a stranded row value withCurrent kept, or a
   * voice newer than the last preview fetch). Groups render only when the
   * manifest has loaded AND the list actually spans more than one — a
   * single-bucket list stays a flat list rather than a list with one redundant
   * heading. 'real' is the pre-2026-09-18 spelling of 'natural', accepted so
   * a stale manifest still groups.
   */
  const natural = options.filter((v) => ['natural', 'real'].includes(manifest?.[v]?.provenance ?? ''))
  const generated = options.filter((v) => manifest?.[v]?.provenance === 'generated')
  const unknown = options.filter((v) => !manifest?.[v]?.provenance)
  const grouped = [natural, generated, unknown].filter((g) => g.length > 0).length > 1

  return (
    <div className="field">
      <Label htmlFor="voice">Voice</Label>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger id="voice" className="w-full max-w-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {grouped ? (
            <>
              {natural.length > 0 && (
                <SelectGroup>
                  <SelectLabel>Natural voices</SelectLabel>
                  {natural.map(item)}
                </SelectGroup>
              )}
              {generated.length > 0 && (
                <SelectGroup>
                  <SelectLabel>Generated voices</SelectLabel>
                  {generated.map(item)}
                </SelectGroup>
              )}
              {unknown.map(item)}
            </>
          ) : (
            options.map(item)
          )}
        </SelectContent>
      </Select>
      {hint && <p className="max-w-md text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

function Choice({
  id,
  label,
  value,
  options,
  onChange,
  hint,
  disabled,
}: {
  id: string
  label: string
  value: string
  options: string[]
  onChange: (v: string) => void
  hint?: string
  disabled?: boolean
}) {
  return (
    <div className="field">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger id={id} className="w-full max-w-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o} value={o}>
              <span className="mono">{o}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hint && <p className="max-w-md text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

/**
 * The sentinel for "no webhook". Radix Select refuses an empty-string item
 * value, so null is represented by this constant instead — safe because real
 * ids are `wh-` + UUID and can never collide with it.
 */
const NO_WEBHOOK = 'none'

/**
 * A nullable webhook reference as a select — `Choice` with two differences it
 * cannot absorb: the value is an ID while the label is the endpoint's NAME, and
 * null is a first-class option rather than a missing one.
 *
 * KEEPS A VALUE IT DOES NOT RECOGNISE, the `withCurrent` rule from the fixed
 * option lists above: when the saved id is not among the live endpoints —
 * revoked since it was chosen, or the list simply failed to load — the current
 * value is rendered as its own option instead of being silently replaced. A
 * form that rewrites a field nobody touched is the worst kind of form; here it
 * would also un-wire a webhook as a side effect of opening the page.
 */
function WebhookSelect({
  id,
  label,
  value,
  hooks,
  onChange,
  disabled,
  hint,
}: {
  id: string
  label: string
  value: string | null
  /** Null while loading or unreachable — the current value still renders. */
  hooks: WebhookView[] | null
  onChange: (v: string | null) => void
  disabled?: boolean
  hint?: string
}) {
  const known = hooks?.some((h) => h.id === value) ?? false
  return (
    <div className="field">
      <Label htmlFor={id}>{label}</Label>
      <Select
        value={value ?? NO_WEBHOOK}
        onValueChange={(v) => onChange(v === NO_WEBHOOK ? null : v)}
        disabled={disabled}
      >
        <SelectTrigger id={id} className="w-full max-w-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NO_WEBHOOK}>
            <span className="text-muted-foreground">None</span>
          </SelectItem>
          {value !== null && !known && (
            <SelectItem value={value}>
              <span className="mono" title="This id is not on the live endpoint list — revoked, or the list failed to load. Kept so opening this page does not rewrite the field.">
                {value}
              </span>
            </SelectItem>
          )}
          {hooks?.map((h) => (
            <SelectItem key={h.id} value={h.id}>
              {h.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hint && <p className="max-w-md text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

/**
 * The numbers that ring THIS agent, and the controls to change that.
 *
 * EVERYTHING HERE IS IMMEDIATE, unlike the whole form above it: attach and
 * detach each PATCH the number row via the telephony client the moment they are
 * clicked, and the page's draft/patch/Save machinery knows nothing about them —
 * a number is not a column on the agent, it is a row on the Telephony page that
 * points here. The local list is updated from each response rather than
 * refetched; the Telephony page remains the authoritative view.
 */
function AgentPhoneNumbers({ agentId, readOnly }: { agentId: string; readOnly: boolean }) {
  const [numbers, setNumbers] = useState<PhoneNumberView[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  /** The sid whose PATCH is in flight; one at a time is plenty here. */
  const [working, setWorking] = useState<string | null>(null)
  /** '' = nothing picked. Radix refuses an empty-string ITEM value, but an
   *  empty select VALUE just shows the placeholder, which is what "pick one"
   *  should look like. */
  const [attachSid, setAttachSid] = useState('')

  useEffect(() => {
    const ac = new AbortController()
    telephony
      .numbers(ac.signal)
      .then((res) => {
        if (ac.signal.aborted) return
        setNumbers(res.rows)
        setLoadError(null)
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return
        setLoadError(telephonyMessageFor(e))
      })
    return () => ac.abort()
  }, [])

  if (loadError) return <p className="err text-sm">{loadError}</p>
  if (numbers === null) return <p className="text-sm text-muted-foreground">Loading numbers…</p>

  if (numbers.length === 0) {
    return (
      <p className="max-w-2xl text-sm text-muted-foreground">
        No phone numbers yet — add Twilio credentials and sync on the{' '}
        <Link to="/telephony" className="underline underline-offset-4">
          Telephony
        </Link>{' '}
        page.
      </p>
    )
  }

  const active = numbers.filter((n) => n.status === 'active')
  const mine = active.filter((n) => n.agentId === agentId)
  // Everything else stays visible but un-pickable: seeing "assigned to Support"
  // in the list answers "where did our number go" without a page hop, and
  // stealing a number from another agent should be a deliberate detach there,
  // not a side effect of an attach here.
  const others = active.filter((n) => n.agentId !== agentId)

  const put = (updated: PhoneNumberView) => {
    setNumbers((prev) =>
      prev === null ? prev : prev.map((n) => (n.sid === updated.sid ? updated : n)),
    )
  }

  const onDetach = (sid: string) => {
    if (working !== null) return
    setWorking(sid)
    setActionError(null)
    telephony
      .assign(sid, null, AbortSignal.timeout(20_000))
      .then((updated) => {
        setWorking(null)
        put(updated)
      })
      .catch((e: unknown) => {
        setWorking(null)
        setActionError(telephonyMessageFor(e))
      })
  }

  const onAttach = () => {
    if (working !== null || attachSid === '') return
    setWorking(attachSid)
    setActionError(null)
    telephony
      .assign(attachSid, agentId, AbortSignal.timeout(20_000))
      .then((updated) => {
        setWorking(null)
        setAttachSid('')
        put(updated)
      })
      .catch((e: unknown) => {
        setWorking(null)
        setActionError(telephonyMessageFor(e))
      })
  }

  return (
    <div className="flex max-w-2xl flex-col gap-3">
      {mine.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No number points at this agent — callers cannot reach it by phone yet.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {mine.map((n) => (
            <div key={n.sid} className="flex flex-wrap items-center gap-3">
              <span className="mono text-sm">{n.number}</span>
              {n.friendlyName !== null && n.friendlyName !== n.number && (
                <span className="truncate text-xs text-muted-foreground" title={n.friendlyName}>
                  {n.friendlyName}
                </span>
              )}
              {n.webhookOk === false && (
                <span
                  className="text-xs text-ev-bad"
                  title="Twilio's Voice URL for this number does not point at this deployment, so inbound calls are not reaching the agent. The Fix button on the Telephony page repairs it."
                >
                  webhook points elsewhere
                </span>
              )}
              {!readOnly && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-6 px-2 text-xs"
                  disabled={working === n.sid}
                  onClick={() => onDetach(n.sid)}
                >
                  {working === n.sid ? 'Detaching…' : 'Detach'}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {!readOnly
        && (others.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <Select value={attachSid} onValueChange={setAttachSid}>
              <SelectTrigger size="sm" className="w-64">
                <SelectValue placeholder="Pick a number to attach…" />
              </SelectTrigger>
              <SelectContent>
                {others.map((n) => (
                  <SelectItem key={n.sid} value={n.sid} disabled={n.agentId !== null}>
                    <span className="mono">{n.number}</span>
                    {n.agentId !== null && (
                      <span className="text-muted-foreground">
                        — assigned to {n.agentName ?? n.agentId}
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              size="sm"
              className="h-7 text-xs"
              onClick={onAttach}
              disabled={attachSid === '' || working !== null}
            >
              {working !== null && working === attachSid ? 'Attaching…' : 'Attach'}
            </Button>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            {active.length > 0
              ? 'Every active number already points here. More can be bought on the '
              : 'Every number on file has been released. Sync or buy on the '}
            <Link to="/telephony" className="underline underline-offset-4">
              Telephony
            </Link>{' '}
            page.
          </p>
        ))}
      {actionError && <p className="err text-sm">{actionError}</p>}
    </div>
  )
}
